import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box,
    Typography,
    Input,
    Table,
    Button,
    Checkbox,
    Modal,
    ModalClose,
    ModalDialog,
    DialogContent,
    DialogTitle,
} from "@mui/joy";
import {
    ArrowDown,
    ArrowUp,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { authFetch } from "../../utils/authFetch";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import OrderDetailsModal from "../../components/ModaleOrdine";

export default function OrdiniMarketplace() {
    const selectedOrg = localStorage.getItem("selectedOrg");
    const [orders, setOrders] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const perPage = 20;
    const [sortConfig, setSortConfig] = useState({
        key: "id",
        direction: "desc",
    });
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [last, setLast] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const openDetails = (order) => {
        setSelectedOrder(order);
        setIsModalOpen(true);
    };

    const fetchOrders = useCallback(async () => {
        try {
            const res = await authFetch(
                `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/marketplace/orders`
            );
            const data = await res.json();
            if (Array.isArray(data)) {
                setOrders(data);
                setLast(data[data.length - 1].id);
            } else {
                toast.error("Errore caricamento ordini");
            }
        } catch (err) {
            console.error(err);
            toast.error("Errore server");
        }
    }, [selectedOrg]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const handleUpdateStatus = async (status, id, planId) => {
      console.log(status, id, planId)
        try {
            const res = await authFetch(
                `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/marketplace/ordersUpdate/${id}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        stato_pagamento: status,
                        piano: planId,
                    }),
                }
            );

            if (!res.ok) throw new Error("Errore aggiornamento");

            toast.success(`Ordine #${id} aggiornato: ${status}`);
            fetchOrders();
        } catch (err) {
            console.error(err);
            toast.error("Errore aggiornamento stato");
        }
    };

    const filtered = useMemo(() => {
        const lower = search.toLowerCase();
        return orders.filter(
            (o) =>
                o.pacchetto_label?.toLowerCase().includes(lower) ||
                o.tipo_operazione?.toLowerCase().includes(lower) ||
                o.id.toString().includes(lower)
        );
    }, [orders, search]);

    const sorted = useMemo(() => {
        return [...filtered].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];
            if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [filtered, sortConfig]);

    const paginatedOrders = useMemo(() => {
        const start = (page - 1) * perPage;
        return sorted.slice(start, start + perPage);
    }, [sorted, page]);

    const totalPages = Math.ceil(sorted.length / perPage);

    const handleSort = (key) => {
        setPage(1);
        setSortConfig((current) => {
            if (current.key === key) {
                return {
                    key,
                    direction: current.direction === "asc" ? "desc" : "asc",
                };
            }
            return { key, direction: "asc" };
        });
    };

    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey)
            return <ArrowDown size={14} color="#bbb" />;
        return sortConfig.direction === "asc" ? (
            <ArrowUp size={14} color="black" />
        ) : (
            <ArrowDown size={14} color="black" />
        );
    };

    return (
        <Box>
            <Typography level="h3" mb={2}>
                Ordini Marketplace
            </Typography>

            <Box mb={2} display="flex" gap={2}>
                <Input
                    placeholder="Cerca ordine..."
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                    }}
                    sx={{ maxWidth: 300 }}
                />
            </Box>

            <Box
                sx={{
                    borderRadius: "lg",
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "neutral.outlinedBorder",
                }}
            >
                <Table
                    size="sm"
                    sx={{
                        minWidth: { xs: "100%", sm: 500 },
                        borderRadius: 0,
                        tableLayout: "auto",
                        "& thead th": {
                            bgcolor: "neutral.softBg",
                            textAlign: "left",
                            px: { xs: 1, sm: 2 },
                            py: { xs: 0.5, sm: 1 },
                            fontSize: { xs: "xs", sm: "sm" },
                            whiteSpace: "nowrap",
                            cursor: "pointer",
                        },
                        "& tbody td": {
                            px: { xs: 1, sm: 2 },
                            py: { xs: 0.5, sm: 1 },
                            fontSize: { xs: "sm", sm: "md" },
                            wordBreak: "break-word",
                        },
                        "& tbody tr:hover": {
                            bgcolor: "neutral.plainHoverBg",
                        },
                    }}
                >
                    <thead>
                        <tr>
                            <th onClick={() => handleSort("id")}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    ID <SortIcon columnKey="id" />
                                </Box>
                            </th>
                            <th>Data</th>
                            <th onClick={() => handleSort("pacchetto_label")}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    Pacchetto{" "}
                                    <SortIcon columnKey="pacchetto_label" />
                                </Box>
                            </th>
                            <th>Operazione</th>
                            <th onClick={() => handleSort("totale_netto")}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    Totale <SortIcon columnKey="totale_netto" />
                                </Box>
                            </th>
                            <th>Stato</th>
                            <th style={{ textAlign: "center" }}>Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedOrders.length > 0 ? (
                            paginatedOrders.map((order) => (
                                <tr
                                    key={order.id}
                                    style={{ cursor: "pointer" }}
                                >
                                    <td >
                                        {order.id}
                                    </td>
                                    <td>
                                        {format(
                                            new Date(order.created_at),
                                            "dd/MM/yyyy",
                                            { locale: it }
                                        )}
                                    </td>
                                    <td onClick={() => openDetails(order)}>{order.pacchetto_label || "-"}</td>
                                    <td>
                                        {order.tipo_operazione
                                            ? order.tipo_operazione
                                                  .charAt(0)
                                                  .toUpperCase() +
                                              order.tipo_operazione.slice(1)
                                            : "-"}
                                    </td>
                                    <td>
                                        {parseFloat(order.totale_netto).toFixed(
                                            2
                                        )}{" "}
                                        €
                                    </td>
                                    <td>
                                        <Typography
                                            level="body-sm"
                                            color={
                                                order.stato_pagamento === "paid"
                                                    ? "success"
                                                    : order.stato_pagamento ===
                                                      "cancelled"
                                                    ? "neutral"
                                                    : "danger"
                                            }
                                        >
                                            {order.stato_pagamento
                                                .charAt(0)
                                                .toUpperCase() +
                                                order.stato_pagamento.slice(1)}
                                        </Typography>
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                        <Checkbox
                                        color="success"
                                            checked={
                                                order.stato_pagamento === "paid"
                                                    ? true
                                                    : false
                                            }
                                            onChange={() => {
                                                if (
                                                    order.stato_pagamento ===
                                                    "pending"
                                                ) {
                                                    handleUpdateStatus(
                                                        "paid",
                                                        order.id,
                                                        order.pacchetto_id
                                                    );
                                                } else {
                                                    setIsOpen(true);
                                                }
                                            }}
                                            disabled={
                                                last === order.id ? false : true
                                            }
                                        />
                                      {last === order.id ? 
                                        <Modal
                                            open={isOpen}
                                            onClose={() => setIsOpen(false)}
                                        >
                                            <ModalDialog>
                                                <ModalClose />
                                                <DialogTitle style={{marginBottom : 30}}>
                                                    <Typography fontSize='xl'>Eliminazione ordine</Typography>
                                                </DialogTitle>
                                                <DialogContent>
                                                    <Typography fontSize='sm'>
                                                        Sei sicuro di voler
                                                        eliminare l'ordine? <br/>
                                                        L'operazione è irreversibile.
                                                    </Typography>
                                                </DialogContent>
                                                <Box
                                                    style={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        marginTop: 20,
                                                    }}
                                                >
                                                    <Button variant="plain" color='neutral' onClick={() => setIsOpen(false)}>
                                                        Cancella
                                                    </Button>
                                                    <Button
                                                        color="danger"
                                                        onClick={() => {
                                                            handleUpdateStatus(
                                                                "cancelled",
                                                                order.id,
                                                                order.pacchetto_id
                                                            );
                                                            setIsOpen(false);
                                                        }}
                                                    >
                                                        Elimina
                                                    </Button>
                                                </Box>
                                            </ModalDialog>
                                        </Modal> : null}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td
                                    colSpan={7}
                                    style={{
                                        textAlign: "center",
                                        padding: "12px",
                                    }}
                                >
                                    Nessun ordine trovato.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Box>

            {totalPages > 1 && (
                <Box
                    mt={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Button
                        size="sm"
                        variant="outlined"
                        disabled={page === 1}
                        onClick={() => setPage((p) => p - 1)}
                        startDecorator={<ChevronLeft size={14} />}
                    >
                        Precedente
                    </Button>

                    <Typography level="body-sm">
                        Pagina {page} di {totalPages}
                    </Typography>

                    <Button
                        size="sm"
                        variant="outlined"
                        disabled={page === totalPages}
                        onClick={() => setPage((p) => p + 1)}
                        endDecorator={<ChevronRight size={14} />}
                    >
                        Successiva
                    </Button>
                </Box>
            )}

            <OrderDetailsModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                order={selectedOrder}
            />
        </Box>
    );
}
